// ── Alert History Engine ─────────────────────────────
// Tracks detected alerts over time in localStorage.
// Identifies recurring (≥3 in 14d) and structural (≥5 in 30d) patterns.
// Never stores judgments. Never exposes raw history to users.

export type AlertCategory = "health" | "overload" | "incoherence" | "missing_data" | "optimization";

export interface HistoryEntry {
  category: AlertCategory;
  date: string; // ISO date (YYYY-MM-DD)
  page: string;
  priority: number;
  resolved: boolean;
}

export type RecurrenceLevel = "isolated" | "recurring" | "structural";

export interface RecurrenceInfo {
  category: AlertCategory;
  level: RecurrenceLevel;
  occurrences14d: number;
  occurrences30d: number;
  firstSeen: string; // ISO date
}

const HISTORY_KEY = "alert-history-v1";
const MAX_ENTRIES = 200; // cap to prevent localStorage bloat
const DAY_MS = 86_400_000;

// ── Storage helpers ──────────────────────────────────

function readHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as HistoryEntry[];
  } catch {
    return [];
  }
}

function writeHistory(entries: HistoryEntry[]) {
  // Prune entries older than 60 days and cap size
  const cutoff = new Date(Date.now() - 60 * DAY_MS).toISOString().slice(0, 10);
  const pruned = entries
    .filter((e) => e.date >= cutoff)
    .slice(-MAX_ENTRIES);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(pruned));
  } catch {
    // localStorage full — silently fail
  }
}

// ── Record today's alerts ────────────────────────────

export function recordAlerts(
  categories: AlertCategory[],
  page: string,
  primaryPriority: number
) {
  const today = new Date().toISOString().slice(0, 10);
  const history = readHistory();

  // Only record once per category per day to avoid spam
  const todayCategories = new Set(
    history.filter((e) => e.date === today).map((e) => e.category)
  );

  const newEntries: HistoryEntry[] = [];
  for (const cat of categories) {
    if (!todayCategories.has(cat)) {
      newEntries.push({
        category: cat,
        date: today,
        page,
        priority: primaryPriority,
        resolved: false,
      });
    }
  }

  if (newEntries.length > 0) {
    writeHistory([...history, ...newEntries]);
  }
}

// ── Mark alerts as resolved ──────────────────────────

export function resolveCategory(category: AlertCategory) {
  const history = readHistory();
  const today = new Date().toISOString().slice(0, 10);

  // Mark recent unresolved entries of this category as resolved
  const updated = history.map((e) => {
    if (e.category === category && !e.resolved && e.date === today) {
      return { ...e, resolved: true };
    }
    return e;
  });

  writeHistory(updated);
}

// ── Analyze recurrence patterns ──────────────────────

export function analyzeRecurrence(): Map<AlertCategory, RecurrenceInfo> {
  const history = readHistory();
  const now = Date.now();
  const d14 = new Date(now - 14 * DAY_MS).toISOString().slice(0, 10);
  const d30 = new Date(now - 30 * DAY_MS).toISOString().slice(0, 10);

  const categories: AlertCategory[] = [
    "health",
    "overload",
    "incoherence",
    "missing_data",
    "optimization",
  ];

  const result = new Map<AlertCategory, RecurrenceInfo>();

  for (const cat of categories) {
    const catEntries = history.filter((e) => e.category === cat);
    if (catEntries.length === 0) continue;

    // Count unique days (not raw entries)
    const days14 = new Set(
      catEntries.filter((e) => e.date >= d14).map((e) => e.date)
    ).size;
    const days30 = new Set(
      catEntries.filter((e) => e.date >= d30).map((e) => e.date)
    ).size;

    const firstSeen = catEntries.reduce(
      (min, e) => (e.date < min ? e.date : min),
      catEntries[0].date
    );

    let level: RecurrenceLevel = "isolated";
    if (days30 >= 5) {
      level = "structural";
    } else if (days14 >= 3) {
      level = "recurring";
    }

    result.set(cat, {
      category: cat,
      level,
      occurrences14d: days14,
      occurrences30d: days30,
      firstSeen,
    });
  }

  return result;
}

// ── Message adaptation based on recurrence ───────────
// Returns a prefix/suffix modifier, never replaces the core message.

export function getRecurrenceModifier(
  category: AlertCategory,
  recurrence: Map<AlertCategory, RecurrenceInfo>
): { prefix: string | null; weeksCount: number | null } {
  const info = recurrence.get(category);
  if (!info || info.level === "isolated") {
    return { prefix: null, weeksCount: null };
  }

  const firstDate = new Date(info.firstSeen);
  const weeks = Math.max(1, Math.round((Date.now() - firstDate.getTime()) / (7 * DAY_MS)));

  if (info.level === "structural") {
    return {
      prefix: `Ce point revient depuis ${weeks} semaine${weeks > 1 ? "s" : ""}`,
      weeksCount: weeks,
    };
  }

  // recurring
  return {
    prefix: "Ce constat se répète",
    weeksCount: weeks,
  };
}
